import {
  Autocomplete,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AccountState, Provider } from '@repo/api-gw-sdk';
import type { AzureAppRegistrationOption } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { CustomAccountDisplayName } from '@/components/form/customAccountDisplayName';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { Loader } from '@/components/layout/loading';
import { AzureTenantSelect } from '@/components/selectors/azureTenantSelect';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import type { AzureRestoreAccountState } from './azureConnectRestoreWizard';

import { StepErrorDialog } from '../../../../../../components/dialogs/stepErrorDialog';
import { BulletItem } from '../../bulletItem';
import { extractErrorMessage } from '../../errorHandling';
import { NumericBulletSection } from '../../numericBulletSection';

const subscriptionIdConfig = getCloudFieldConfig(
  Provider.Azure,
  'subscriptionId'
);
const resourceGroupNameConfig = getCloudFieldConfig(
  Provider.Azure,
  'resourceGroupName'
);

const DEFAULT_RESTORE_INTERNAL_RG_NAME = 'eon-restore-internal-rg';

export const ConnectAzureRestoreAccount = (
  props: StepProps<AzureRestoreAccountState>
) => {
  const { AzureMultipleAppRegistrations, AzureCustomPermissionsModel } =
    useFeatureFlags();
  const [isDeploying, setIsDeploying] = useState(false);
  const [customName, setCustomName] = useState('');
  const dal = useDAL();

  const { data: backupApps, isLoading } = dal.cloudAccounts.azureApps.useApps(
    'Restore',
    props.wizardState.tenantId
  );
  const { data: appRegistrations } =
    dal.cloudAccounts.azureApps.useListAppRegistrations(
      AzureMultipleAppRegistrations ? props.wizardState.tenantId : undefined
    );
  const [selectedMgmtAppId, setSelectedMgmtAppId] = useState('');
  const [selectedDaAppId, setSelectedDaAppId] = useState('');

  const [error, setError] = useState<Error | null>(null);

  const managementApps = useMemo(
    () =>
      appRegistrations?.appRegistrations?.filter(
        (app: AzureAppRegistrationOption) =>
          app.applicationType === 'ManagementApp'
      ) || [],
    [appRegistrations]
  );
  const dataAccessApps = useMemo(
    () =>
      appRegistrations?.appRegistrations?.filter(
        (app: AzureAppRegistrationOption) =>
          app.applicationType === 'DataAccessApp'
      ) || [],
    [appRegistrations]
  );

  const effectiveMgmtAppId = AzureMultipleAppRegistrations
    ? (managementApps.find(
        (a: AzureAppRegistrationOption) => a.id === selectedMgmtAppId
      )?.applicationId ?? '')
    : backupApps?.managementAppId;
  const effectiveDaAppId = AzureMultipleAppRegistrations
    ? (dataAccessApps.find(
        (a: AzureAppRegistrationOption) => a.id === selectedDaAppId
      )?.applicationId ?? '')
    : backupApps?.backupAppId;

  const resourceGroupName =
    props.wizardState.restoreConnectType === 'resourceGroup'
      ? (props.wizardState.restoreResourceGroupName ?? '')
      : '';

  const bashCode = `\
  # Login to the tenant
  az login --tenant "${props.wizardState.tenantId}"

  # Set the subscription
  az account set --subscription "${props.wizardState.restoreSubscriptionId}"

  # Get tokens in the correct context
  ARM_TOKEN=$(az account get-access-token --query accessToken -o tsv)
  GRAPH_TOKEN=$(az account get-access-token --resource https://graph.microsoft.com/ --query accessToken -o tsv)

  # Download and run restore script
  curl -fsSL https://eon.blob.core.windows.net/public/onboarding/latest/restore/bash/restore.sh -o restore.sh && chmod +x restore.sh && \\
  AZURE_ACCESS_TOKEN="$ARM_TOKEN" \\
  AZURE_GRAPH_TOKEN="$GRAPH_TOKEN" \\
  ./restore.sh \\
    --subscription-id "${props.wizardState.restoreSubscriptionId}" \\
    --management-app-id "${effectiveMgmtAppId ?? ''}" \\
    --backup-app-id "${effectiveDaAppId ?? ''}"${
      backupApps?.adxClusterManagedIdentityPrincipalId
        ? ` \\\n    --adx-principal-id "${backupApps?.adxClusterManagedIdentityPrincipalId ?? ''}"`
        : ''
    }${
      props.wizardState.restoreConnectType === 'resourceGroup'
        ? ` \\\n    --resource-group "${resourceGroupName ?? ''}"`
        : ''
    }`;

  const terraformCode = `\
  module "azure_restore" {
    source = "https://eon.blob.core.windows.net/public/onboarding/latest/restore/terraform/restore.zip"
    subscription_id                  = "${props.wizardState.restoreSubscriptionId ?? ''}"
    management_app_id                = "${effectiveMgmtAppId ?? ''}"
    backup_restore_app_id            = "${effectiveDaAppId ?? ''}"${
      backupApps?.adxClusterManagedIdentityPrincipalId
        ? `\n    adx_service_principal_id         = "${backupApps?.adxClusterManagedIdentityPrincipalId ?? ''}"`
        : ''
    }${
      props.wizardState.restoreConnectType === 'resourceGroup'
        ? `\n    resource_group_name              = "${resourceGroupName ?? ''}"`
        : ''
    }
  }

  output "restore_onboarding" {
    value = module.azure_restore
  }`;

  const code =
    props.wizardState.restoreConnectMethod === 'terraform'
      ? terraformCode
      : bashCode;

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      const { restoreAccount } = await dal.cloudAccounts.restore.connect.azure({
        tenantId: props.wizardState.tenantId,
        restoreSubscriptionId: props.wizardState.restoreSubscriptionId,
        restoreResourceGroupName: resourceGroupName || undefined,
        eonInternalResourceGroupName:
          props.wizardState.eonInternalResourceGroupName || undefined,
        name: customName || undefined,
        managementAppRegistrationId:
          AzureMultipleAppRegistrations && selectedMgmtAppId
            ? selectedMgmtAppId
            : undefined,
        dataAccessAppRegistrationId:
          AzureMultipleAppRegistrations && selectedDaAppId
            ? selectedDaAppId
            : undefined,
      });

      if (
        restoreAccount.status &&
        restoreAccount.status !== AccountState.ACCOUNT_STATE_CONNECTED
      ) {
        console.log(restoreAccount);
        setError(new Error('Failed to deploy source account'));
      } else {
        setIsDeploying(false);
        await props.finish();
      }
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
      setIsDeploying(false);
    }
  };

  const missingFirstStepFields =
    !props.wizardState.tenantId ||
    !props.wizardState.restoreSubscriptionId ||
    (props.wizardState.restoreConnectType === 'resourceGroup' &&
      !props.wizardState.restoreResourceGroupName);

  const missingSecondStepFields =
    !props.wizardState.eonInternalResourceGroupName;

  const isMissingFields = missingFirstStepFields || missingSecondStepFields;

  const validateForm = () => {
    const validationErrors =
      !subscriptionIdConfig.validation.validateInput(
        props.wizardState.restoreSubscriptionId
      ) ||
      (props.wizardState.restoreConnectType === 'resourceGroup' &&
        props.wizardState.restoreResourceGroupName &&
        !resourceGroupNameConfig.validation.validateInput(
          props.wizardState.restoreResourceGroupName
        )) ||
      (props.wizardState.eonInternalResourceGroupName &&
        !resourceGroupNameConfig.validation.validateInput(
          props.wizardState.eonInternalResourceGroupName
        ));

    if (validationErrors && isMissingFields) {
      return 'All fields are required, and some are in the wrong format. Fix the issues so you can proceed.';
    }

    if (validationErrors) {
      return 'Some fields are in the wrong format. Fix the issues so you can proceed.';
    }

    if (isMissingFields) {
      return 'All fields are required, but some are missing values. Fill in all fields so you can proceed.';
    }

    if (
      AzureMultipleAppRegistrations &&
      (!selectedMgmtAppId || !selectedDaAppId)
    ) {
      return 'Please select both a Management App and a Data Access App.';
    }

    return '';
  };

  const nextButtonTooltip = validateForm();

  const bulletTitle = AzureCustomPermissionsModel
    ? 'Select the restore subscription you want to connect and the app registrations to use.'
    : `Tell us which restore subscription you want to connect, and then
                  ${
                    props.wizardState.restoreConnectMethod === 'terraform'
                      ? 'apply the generated Terraform module'
                      : 'run the generated Bash command'
                  } in your environment.`;

  return (
    <>
      <StepContainer
        stepperIndex={3}
        hideStepper={true}
        stepperLabels={props.stepperLabels}
        canGoNext={() => {
          return !isDeploying && !nextButtonTooltip;
        }}
        nextButtonDisabledTooltip={nextButtonTooltip}
        nextButtonText={'Connect'}
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingTop: '12px', paddingX: '0px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px' width='100%' height='auto'>
          {AzureCustomPermissionsModel ? (
            <Stack sx={{ paddingX: 'min(100px,6vw)', paddingTop: '16px' }}>
              <Typography variant='body2'>
                Select the restore subscription and choose the app registrations
                to use. Make sure the selected apps have the required roles
                assigned on this subscription and its internal resource group
                before connecting.
              </Typography>
            </Stack>
          ) : (
            <TerraformBashToggle
              text={`Before you start, make sure you can manage service principals in your Azure environment.`}
              connectMethod={props.wizardState.restoreConnectMethod}
              setConnectMethod={(connectMethod) => {
                props.setWizardState({
                  ...props.wizardState,
                  restoreConnectMethod: connectMethod,
                });
              }}
            />
          )}
          <Stack
            paddingX='min(100px,6vw)'
            paddingTop='16px'
            width='100%'
            height='auto'
          >
            {isLoading ? (
              <Loader />
            ) : (
              <Stack
                gap='40px'
                divider={<Divider sx={{ borderStyle: 'dashed' }} />}
              >
                <NumericBulletSection
                  bulletNumber={1}
                  title={
                    AzureCustomPermissionsModel
                      ? 'Connect restore subscription'
                      : "Deploy Eon's restore subscription"
                  }
                >
                  <BulletItem
                    title={bulletTitle}
                    leftContent={
                      <>
                        <Stack
                          gap='24px'
                          divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                        >
                          <Stack gap='24px'>
                            <AzureTenantSelect
                              tenantId={props.wizardState.tenantId}
                              onChange={(tenantId) => {
                                setSelectedMgmtAppId('');
                                setSelectedDaAppId('');
                                props.setWizardState({
                                  ...props.wizardState,
                                  tenantId,
                                });
                              }}
                            />
                            <FormTextField
                              title='Subscription ID'
                              placeholder={subscriptionIdConfig.placeholder}
                              validation={subscriptionIdConfig.validation}
                              value={
                                props.wizardState.restoreSubscriptionId || ''
                              }
                              onChange={(event) =>
                                props.setWizardState({
                                  ...props.wizardState,
                                  restoreSubscriptionId: event.target.value,
                                })
                              }
                            />
                            <FormControl>
                              <FormLabel>
                                <Typography variant='subtitle1'>
                                  Allow Eon to restore to
                                </Typography>
                              </FormLabel>
                              <ToggleButtonGroup
                                sx={{ width: 'max-content' }}
                                exclusive
                                value={props.wizardState.restoreConnectType}
                                onChange={(_, value) => {
                                  props.setWizardState({
                                    ...props.wizardState,
                                    restoreConnectType: value,
                                  });
                                }}
                              >
                                <ToggleButton value='subscription'>
                                  All resource groups
                                </ToggleButton>
                                <ToggleButton value='resourceGroup'>
                                  Specific resource group
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </FormControl>
                            {props.wizardState.restoreConnectType ===
                              'resourceGroup' && (
                              <FormTextField
                                title='Resource group name'
                                placeholder={
                                  resourceGroupNameConfig.placeholder
                                }
                                validation={resourceGroupNameConfig.validation}
                                value={
                                  props.wizardState.restoreResourceGroupName ||
                                  ''
                                }
                                onChange={(event) =>
                                  props.setWizardState({
                                    ...props.wizardState,
                                    restoreResourceGroupName:
                                      event.target.value,
                                  })
                                }
                              />
                            )}
                          </Stack>
                          {AzureMultipleAppRegistrations &&
                            managementApps.length > 0 && (
                              <Stack gap='16px'>
                                <Typography variant='subtitle1'>
                                  App Registrations
                                </Typography>
                                <FormControl size='small'>
                                  <FormLabel>Management App</FormLabel>
                                  <Autocomplete
                                    size='small'
                                    options={managementApps}
                                    value={
                                      managementApps.find(
                                        (a: AzureAppRegistrationOption) =>
                                          a.id === selectedMgmtAppId
                                      ) || null
                                    }
                                    onChange={(_, val) =>
                                      setSelectedMgmtAppId(val?.id || '')
                                    }
                                    getOptionLabel={(
                                      opt: AzureAppRegistrationOption
                                    ) =>
                                      opt.displayName
                                        ? `${opt.displayName} (${opt.applicationId})`
                                        : opt.applicationId
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder='Select management app'
                                      />
                                    )}
                                  />
                                </FormControl>
                                <FormControl size='small'>
                                  <FormLabel>Data Access App</FormLabel>
                                  <Autocomplete
                                    size='small'
                                    options={dataAccessApps}
                                    value={
                                      dataAccessApps.find(
                                        (a: AzureAppRegistrationOption) =>
                                          a.id === selectedDaAppId
                                      ) || null
                                    }
                                    onChange={(_, val) =>
                                      setSelectedDaAppId(val?.id || '')
                                    }
                                    getOptionLabel={(
                                      opt: AzureAppRegistrationOption
                                    ) =>
                                      opt.displayName
                                        ? `${opt.displayName} (${opt.applicationId})`
                                        : opt.applicationId
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder='Select data access app'
                                      />
                                    )}
                                  />
                                </FormControl>
                              </Stack>
                            )}
                          {AzureCustomPermissionsModel && (
                            <FormTextField
                              title='Internal Resource Group Name'
                              placeholder='e.g. eon-restore-internal-rg'
                              validation={resourceGroupNameConfig.validation}
                              value={
                                props.wizardState
                                  .eonInternalResourceGroupName || ''
                              }
                              onChange={(event) =>
                                props.setWizardState({
                                  ...props.wizardState,
                                  eonInternalResourceGroupName:
                                    event.target.value,
                                })
                              }
                            />
                          )}
                          <CustomAccountDisplayName
                            entityName='subscription'
                            customName={customName}
                            setCustomName={setCustomName}
                          />
                        </Stack>
                      </>
                    }
                    rightContent={
                      AzureCustomPermissionsModel ? (
                        <Stack
                          gap='16px'
                          sx={{
                            padding: '24px',
                            backgroundColor:
                              'color-mix(in srgb, var(--mui-palette-warning-main) 6%, transparent)',
                            borderRadius: '8px',
                            borderLeft:
                              '4px solid var(--mui-palette-warning-main)',
                          }}
                        >
                          <Typography
                            variant='subtitle1'
                            sx={{ fontWeight: 500 }}
                          >
                            Pre-connection checklist
                          </Typography>
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'var(--mui-palette-text-secondary)',
                            }}
                          >
                            Before connecting, please verify:
                          </Typography>
                          <Stack
                            component='ul'
                            gap='12px'
                            sx={{ margin: 0, paddingLeft: '20px' }}
                          >
                            <Typography component='li' variant='body2'>
                              The selected management and data access apps exist
                              in your tenant
                            </Typography>
                            <Typography component='li' variant='body2'>
                              The apps have the required roles assigned on this
                              subscription
                            </Typography>
                            <Typography component='li' variant='body2'>
                              The apps have the required roles assigned on the
                              resource group you allocated for Eon in your
                              subscription
                            </Typography>
                          </Stack>
                        </Stack>
                      ) : (
                        <CopyableCode
                          title={
                            props.wizardState.restoreConnectMethod ===
                            'terraform'
                              ? 'main.tf'
                              : 'restore.sh'
                          }
                          code={code}
                          blur={missingFirstStepFields}
                        />
                      )
                    }
                  />
                </NumericBulletSection>
                {!AzureCustomPermissionsModel && (
                  <NumericBulletSection
                    bulletNumber={2}
                    title='Complete configuration'
                  >
                    <BulletItem
                      title={`After running the ${
                        props.wizardState.restoreConnectMethod === 'terraform'
                          ? 'Terraform module'
                          : 'Bash command'
                      }, paste the output value below:`}
                      leftContent={
                        <Stack gap='8px' alignItems='flex-start'>
                          <FormTextField
                            title='eon_internal_resource_group_name'
                            placeholder={DEFAULT_RESTORE_INTERNAL_RG_NAME}
                            validation={resourceGroupNameConfig.validation}
                            value={
                              props.wizardState.eonInternalResourceGroupName ||
                              ''
                            }
                            onChange={(event) =>
                              props.setWizardState({
                                ...props.wizardState,
                                eonInternalResourceGroupName:
                                  event.target.value,
                              })
                            }
                          />
                          <Button
                            size='small'
                            variant='text'
                            onClick={() =>
                              props.setWizardState({
                                ...props.wizardState,
                                eonInternalResourceGroupName:
                                  DEFAULT_RESTORE_INTERNAL_RG_NAME,
                              })
                            }
                          >
                            Use default name
                          </Button>
                        </Stack>
                      }
                    />
                  </NumericBulletSection>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
